import type { Suggestion } from '@atlassian/editor-plugin-ai-suggestions';

import { orderSuggestionsByDocument } from './orderSuggestionsByDocument.ts';
import { fetchLatestDraftAdf, fetchPublishedPageSuggestions } from './publishedSuggestionsApi.ts';

export type PublishedSuggestionsStatus = 'idle' | 'loading' | 'loaded' | 'error';

export type PublishedSuggestionsState = {
	contentId?: string;
	status: PublishedSuggestionsStatus;
	suggestions: Suggestion[];
	selectedSuggestionId?: string;
	shouldStartReviewInEditMode?: boolean;
};

const initialState: PublishedSuggestionsState = {
	status: 'idle',
	suggestions: [],
};

export const PUBLISHED_SUGGESTIONS_CACHE_TTL_MS: number = 2 * 60 * 1000;

type CachedSuggestions = {
	contentAri: string;
	contentId: string;
	suggestions: Suggestion[];
	loadedAt: number;
};

let snapshot: PublishedSuggestionsState = initialState;
let abortController: AbortController | undefined;
let cache: CachedSuggestions | undefined;

const getFreshCachedSuggestions = (contentAri: string) =>
	cache &&
	cache.contentAri === contentAri &&
	Date.now() - cache.loadedAt < PUBLISHED_SUGGESTIONS_CACHE_TTL_MS
		? cache.suggestions
		: undefined;
const listeners = new Set<() => void>();

const setSnapshot = (next: PublishedSuggestionsState) => {
	snapshot = next;
	listeners.forEach((listener) => listener());
};

export const subscribeToPublishedSuggestions = (onStoreChange: () => void): (() => void) => {
	listeners.add(onStoreChange);
	return () => {
		listeners.delete(onStoreChange);
	};
};

export const getPublishedSuggestionsSnapshot = (): PublishedSuggestionsState => snapshot;

export const getInitialPublishedSuggestionsSnapshot = (): PublishedSuggestionsState => initialState;

export const startPublishedSuggestions = (contentId: string, contentAri: string): void => {
	abortController?.abort();
	abortController = undefined;

	const cachedSuggestions = getFreshCachedSuggestions(contentAri);
	if (cachedSuggestions) {
		setSnapshot({ contentId, status: 'loaded', suggestions: cachedSuggestions });
		return;
	}

	const controller = new AbortController();
	abortController = controller;

	setSnapshot({ contentId, status: 'loading', suggestions: [] });

	const { signal } = controller;
	Promise.all([
		fetchPublishedPageSuggestions({ contentAri, signal }),
		fetchLatestDraftAdf({ contentId, signal }).catch(() => undefined),
	]).then(
		([fetchedSuggestions, latestDraftAdf]) => {
			if (abortController !== controller) {
				return;
			}
			abortController = undefined;

			let suggestions = fetchedSuggestions;
			if (latestDraftAdf) {
				try {
					suggestions = orderSuggestionsByDocument(fetchedSuggestions, latestDraftAdf);
				} catch {}
			}
			cache = { contentAri, contentId, suggestions, loadedAt: Date.now() };
			setSnapshot({ ...snapshot, status: 'loaded', suggestions });
		},
		() => {
			if (abortController !== controller) {
				return;
			}
			abortController = undefined;
			setSnapshot({ ...snapshot, status: 'error', suggestions: [] });
		},
	);
};

export const invalidatePublishedSuggestionsCache = (contentId: string): void => {
	if (cache?.contentId === contentId) {
		cache = undefined;
	}
};

export const selectPublishedSuggestion = (suggestionId: string | undefined): void => {
	if (snapshot.contentId === undefined || snapshot.selectedSuggestionId === suggestionId) {
		return;
	}
	setSnapshot({ ...snapshot, selectedSuggestionId: suggestionId });
};

export const requestPublishedReviewInEditMode = (): void => {
	if (snapshot.contentId === undefined || snapshot.shouldStartReviewInEditMode) {
		return;
	}
	setSnapshot({ ...snapshot, shouldStartReviewInEditMode: true });
};

export const resetPublishedSuggestions = (): void => {
	abortController?.abort();
	abortController = undefined;
	if (snapshot !== initialState) {
		setSnapshot(initialState);
	}
};

export const clearPublishedSuggestionsForTesting = (): void => {
	abortController?.abort();
	abortController = undefined;
	cache = undefined;
	snapshot = initialState;
	listeners.clear();
};
